import { useState, useRef, useEffect } from 'react'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

/* ─── News Articles ─────────────────────────────────────────── */
// Default static coverage cards shown while loading or if RSS feed is empty
const DEFAULT_NEWS_ARTICLES = [
  {
    image: '/wp-content/uploads/2025/07/villa-1900.webp',
    publication: 'Khaleej Times',
    pubColor: '#003087',
    date: 'March 15, 2025',
    headline: 'Victory Homes Records Dh120 Million in Q1 Sales Across Dubai\'s Premium Districts',
    excerpt: 'Victory Homes Real Estate has reported record-breaking Q1 sales figures, driven by surging demand in Palm Jumeirah, Downtown Dubai, and Dubai Hills Estate.',
    link: '#',
  },
  {
    image: '/wp-content/uploads/2025/06/category-footer2-webp.webp',
    publication: 'Gulf News',
    pubColor: '#cc0000',
    date: 'February 28, 2025',
    headline: 'Dubai Off-Plan Market Booms: Victory Homes Secures Landmark Transactions',
    excerpt: 'As Dubai\'s off-plan sector continues its record trajectory, Victory Homes has emerged as a key brokerage connecting investors with exclusive pre-launch opportunities.',
    link: '#',
  },
  {
    image: '/wp-content/uploads/2025/06/uae-project-2-webp.webp',
    publication: 'The National',
    pubColor: '#1a1a1a',
    date: 'January 20, 2025',
    headline: 'How Victory Homes Is Redefining the Luxury Property Experience in the UAE',
    excerpt: 'With a focus on premium client service, market transparency, and curated property portfolios, Victory Homes is setting new standards for real estate consultancy in Dubai.',
    link: '#',
  },
  {
    image: '/wp-content/uploads/2025/07/demo-1-cropped-webp.webp',
    publication: 'Arabian Business',
    pubColor: '#b8860b',
    date: 'December 10, 2024',
    headline: 'Top UAE Real Estate Brokerages of 2024: Victory Homes Features Among the Elite',
    excerpt: 'Arabian Business highlights Victory Homes as one of Dubai\'s standout real estate firms this year, citing consistent transaction volumes and client satisfaction.',
    link: '#',
  },
]

const FALLBACK_IMAGES = [
  '/wp-content/uploads/2025/07/villa-1900.webp',
  '/wp-content/uploads/2025/06/category-footer2-webp.webp',
  '/wp-content/uploads/2025/06/uae-project-2-webp.webp',
  '/wp-content/uploads/2025/07/demo-1-cropped-webp.webp',
]

/* ─── Media logo ticker ─────────────────────────────────────── */
const MEDIA = [
  { name: 'Khaleej Times',     accent: '#003087' },
  { name: 'Gulf News',         accent: '#cc0000' },
  { name: 'The National',      accent: '#1a1a1a' },
  { name: 'Arabian Business',  accent: '#b8860b' },
  { name: 'Zawya',             accent: '#0068b5' },
  { name: 'Construction Week', accent: '#e84118' },
  { name: 'MENAFN',            accent: '#2c7a2c' },
  { name: 'Emirates 247',      accent: '#003087' },
  { name: 'Bayut',             accent: '#7c3aed' },
  { name: 'Property Finder',   accent: '#005e5e' },
]
const TICKER = [...MEDIA, ...MEDIA]

/* ─── NewsCard ──────────────────────────────────────────────── */
function NewsCard({ article, readArticle }) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: hovered
          ? '0 16px 48px rgba(0,46,83,0.15)'
          : '0 2px 16px rgba(0,46,83,0.07)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'all 0.3s cubic-bezier(0.34, 1.2, 0.64, 1)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        cursor: 'pointer',
      }}
      onClick={() => article.link !== '#' && window.open(article.link, '_blank')}
    >
      {/* Image */}
      <div style={{ position: 'relative', height: '210px', overflow: 'hidden', flexShrink: 0 }}>
        <img
          src={article.image}
          alt={article.headline}
          style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
            transform: hovered ? 'scale(1.06)' : 'scale(1)',
            transition: 'transform 0.5s ease',
          }}
        />
        {/* Publication badge over image */}
        <div style={{
          position: 'absolute', bottom: '12px', right: '12px',
          background: '#fff',
          borderRadius: '6px',
          padding: '5px 12px',
          fontFamily: "'Georgia', serif",
          fontSize: '12px',
          fontWeight: 700,
          color: article.pubColor,
          boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
          letterSpacing: '0.3px',
          border: `1px solid ${article.pubColor}22`,
        }}>
          {article.publication}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '22px 24px 24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{
          fontFamily: FONT, fontSize: '12px', color: '#8a9ab0',
          marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: GOLD, display: 'inline-block', flexShrink: 0 }} />
          {article.date}
        </div>

        <h3 style={{
          fontFamily: FONT, fontSize: '15px', fontWeight: 700,
          color: NAVY, margin: '0 0 10px', lineHeight: '1.45',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {article.headline}
        </h3>

        <p style={{
          fontFamily: FONT, fontSize: '13px', color: '#5a6577',
          margin: '0 0 18px', lineHeight: '1.65',
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          flex: 1,
        }}>
          {article.excerpt}
        </p>

        <div style={{
          fontFamily: FONT, fontSize: '13px', fontWeight: 600,
          color: GOLD, display: 'flex', alignItems: 'center', gap: '5px',
          borderTop: '1px solid #f0f3f7', paddingTop: '14px',
        }}>
          { readArticle }
          <span style={{
            transform: hovered ? 'translateX(4px)' : 'translateX(0)',
            transition: 'transform 0.25s ease', display: 'inline-block',
          }}>→</span>
        </div>
      </div>
    </div>
  )
}

/* ─── Newsletter ────────────────────────────────────────────── */
function Newsletter() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle')
  const { t } = useLanguage()

  const handleSubscribe = (e) => {
    e.preventDefault()
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setStatus('error'); setTimeout(() => setStatus('idle'), 3000); return
    }
    setStatus('success'); setEmail('')
    setTimeout(() => setStatus('idle'), 4000)
  }

  return (
    <div style={{
      background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`,
      padding: '56px 20px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: `${GOLD}12`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-80px', left: '5%', width: '200px', height: '200px', borderRadius: '50%', background: `${GOLD}08`, pointerEvents: 'none' }} />

      <div style={{ maxWidth: '820px', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <span style={{
          display: 'inline-block', background: `${GOLD}22`, color: GOLD,
          fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
          padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
          border: `1px solid ${GOLD}40`, marginBottom: '14px',
        }}>{ t('newsletter.badge') }</span>

        <h2 style={{
          fontFamily: FONT, fontSize: 'clamp(22px, 3vw, 34px)', fontWeight: 700,
          color: '#fff', margin: '0 0 10px', lineHeight: 1.25,
        }}>
          { t('newsletter.heading') }
        </h2>
        <p style={{
          fontFamily: FONT, fontSize: '15px', color: 'rgba(255,255,255,0.6)',
          margin: '0 0 30px', lineHeight: 1.7, maxWidth: '500px',
          marginLeft: 'auto', marginRight: 'auto',
        }}>
          { t('newsletter.desc') }
        </p>

        <form onSubmit={handleSubscribe} noValidate>
          <div className="nl-form-row">
            <style>{`
              .nl-inp { flex:1; padding:15px 20px; border:1.5px solid rgba(255,255,255,0.18); border-radius:10px 0 0 10px; font-family:${FONT}; font-size:14px; color:${NAVY}; background:#fff; outline:none; transition:border-color 0.2s; min-width:0; }
              .nl-inp:focus { border-color:${NAVY}; }
              .nl-inp::placeholder { color:#aab4c0; }
              .nl-sbtn { padding:15px 28px; background:${GOLD}; color:#fff; font-family:${FONT}; font-size:14px; font-weight:700; border:none; border-radius:0 10px 10px 0; cursor:pointer; white-space:nowrap; transition:background 0.2s,transform 0.15s; }
              .nl-sbtn:hover { background:#d4af6a; transform:translateX(2px); }
              .nl-form-row { display:flex; max-width:520px; margin:0 auto; }
              @media (max-width: 500px) {
                .nl-form-row { flex-direction: column; }
                .nl-inp { border-radius: 10px 10px 0 0; }
                .nl-sbtn { border-radius: 0 0 10px 10px; }
              }
            `}</style>
            <input className="nl-inp" type="email" placeholder={t('newsletter.placeholder')}
              value={email} onChange={e => { setEmail(e.target.value); setStatus('idle') }} />
            <button type="submit" className="nl-sbtn">{ t('newsletter.btn') }</button>
          </div>
          {status === 'success' && <p style={{ fontFamily: FONT, fontSize: '13px', color: '#86efac', marginTop: '12px' }}>{ t('newsletter.success') }</p>}
          {status === 'error' && <p style={{ fontFamily: FONT, fontSize: '13px', color: '#f87171', marginTop: '12px' }}>{ t('newsletter.error') }</p>}
        </form>
        <p style={{ fontFamily: FONT, fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginTop: '16px', marginBottom: 0 }}>
          { t('newsletter.privacy') }
        </p>
      </div>
    </div>
  )
}

/* ─── Main MediaSection ─────────────────────────────────────── */
function MediaSection() {
  const scrollRef = useRef(null)
  const { t } = useLanguage()

  const [articles, setArticles] = useState(DEFAULT_NEWS_ARTICLES)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchNews() {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || ''
        const res = await fetch(`${apiUrl}/api/news`)
        if (res.ok) {
          const data = await res.json()
          if (data && data.length > 0) {
            // Apply fallback images if missing
            const articlesWithImages = data.map((item, i) => ({
              ...item,
              image: item.image || FALLBACK_IMAGES[i % FALLBACK_IMAGES.length]
            }))
            // Show up to 8 real items
            setArticles(articlesWithImages.slice(0, 8))
          }
        }
      } catch (err) {
        console.error('Failed to fetch real-time news:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchNews()
  }, [])

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 380, behavior: 'smooth' })
    }
  }

  return (
    <div style={{ width: '100%', background: '#f4f7fb' }}>
      <style>{`
        .media-ticker-wrap { overflow:hidden; width:100%; mask-image:linear-gradient(to right,transparent 0%,black 7%,black 93%,transparent 100%); -webkit-mask-image:linear-gradient(to right,transparent 0%,black 7%,black 93%,transparent 100%); }
        .media-ticker-track { display:flex; animation:ticker-scroll 42s linear infinite; will-change:transform; }
        .media-ticker-track:hover { animation-play-state:paused; }
        @keyframes ticker-scroll { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        .media-logo-pill { display:flex; align-items:center; justify-content:center; padding:16px 40px; flex-shrink:0; border-right:1px solid rgba(0,46,83,0.08); transition:background 0.2s; }
        .media-logo-pill:hover { background:rgba(197,160,89,0.07); }
        .media-logo-text { font-family:'Georgia',serif; font-size:14px; font-weight:700; letter-spacing:0.5px; white-space:nowrap; opacity:0.5; transition:opacity 0.2s; }
        .media-logo-pill:hover .media-logo-text { opacity:1; }
        .news-scroll-container { display:flex; gap:24px; overflow-x:auto; scroll-snap-type:x mandatory; -ms-overflow-style:none; scrollbar-width:none; padding-bottom:8px; }
        .news-scroll-container::-webkit-scrollbar { display:none; }
        .news-card-wrap { flex:0 0 calc(25% - 18px); min-width:280px; scroll-snap-align:start; display:flex; flex-direction:column; height:auto; }
        .news-arrow-btn { width:42px;height:42px;border-radius:50%;border:1.5px solid rgba(0,46,83,0.18);background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.2s;font-size:16px;color:${NAVY}; flex-shrink:0; }
        .news-arrow-btn:hover { background:${NAVY}; color:#fff; border-color:${NAVY}; }
        @media (max-width: 1024px) { .news-card-wrap { flex: 0 0 calc(50% - 12px); } }
        @media (max-width: 580px)  { .news-card-wrap { flex: 0 0 calc(100% - 0px); } }
      `}</style>

      {/* ── Featured In News ── */}
      <div style={{ maxWidth: '1300px', margin: '0 auto', padding: '64px 20px 54px', boxSizing: 'border-box' }}>

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '36px', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <span style={{
              display: 'inline-block', background: `${GOLD}18`, color: GOLD,
              fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
              padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
              border: `1px solid ${GOLD}35`, marginBottom: '10px',
            }}>{ t('media.tag') }</span>
            <h2 style={{
              fontFamily: FONT, fontSize: 'clamp(23px, 3vw, 35px)', fontWeight: 700,
              color: NAVY, margin: 0, letterSpacing: '-0.3px', lineHeight: 1.2,
            }}>{ t('media.heading') }</h2>
          </div>

          {/* Arrows */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="news-arrow-btn" onClick={() => scroll(-1)} aria-label="Previous">‹</button>
            <button className="news-arrow-btn" onClick={() => scroll(1)} aria-label="Next">›</button>
          </div>
        </div>

        {/* Cards */}
        <div className="news-scroll-container" ref={scrollRef}>
          {articles.map((article, i) => (
            <div key={i} className="news-card-wrap">
              <NewsCard article={article} readArticle={t('media.readArticle')} />
            </div>
          ))}
        </div>
      </div>

      {/* ── Logo Ticker ── */}
      <div style={{
        borderTop: '1px solid rgba(0,46,83,0.07)',
        borderBottom: '1px solid rgba(0,46,83,0.07)',
        background: '#fff', padding: '6px 0',
      }}>
        <div className="media-ticker-wrap">
          <div className="media-ticker-track">
            {TICKER.map((pub, i) => (
              <div key={i} className="media-logo-pill">
                <span className="media-logo-text" style={{ color: pub.accent }}>{pub.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Newsletter ── */}
      <Newsletter />
    </div>
  )
}

export default MediaSection
